import React, { useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Star, ArrowRight, MessageCircle, Phone } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { COMPANY, IMG } from "@/data/site";
import { useLead } from "@/context/LeadContext";

const Rain = () => {
  const drops = useMemo(
    () => Array.from({ length: 60 }).map(() => ({
      left: Math.random() * 100,
      delay: Math.random() * 2,
      dur: 0.6 + Math.random() * 0.8,
      h: 40 + Math.random() * 80,
      o: 0.15 + Math.random() * 0.4,
    })),
    []
  );
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {drops.map((d, i) => (
        <span
          key={i}
          className="rain-drop"
          style={{ left: `${d.left}%`, height: `${d.h}px`, animationDuration: `${d.dur}s`, animationDelay: `${d.delay}s`, opacity: d.o }}
        />
      ))}
    </div>
  );
};

const badges = [
  { icon: ShieldCheck, label: "15-Year Warranty" },
  { icon: Star, label: "4.9★ Google Rating" },
  { label: "12+ Years", sub: "Experience" },
  { label: "470+", sub: "Projects" },
  { label: "500+", sub: "Customers" },
  { label: "24/7", sub: "Support" },
];

export default function CinematicHero() {
  const { openLead } = useLead();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 160]);
  const contentY = useTransform(scrollY, [0, 700], [0, 80]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-navy-deep" data-testid="hero">
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-20 -bottom-20">
        <img src={IMG.heroBuilding} alt="Luxury buildings in Jaipur protected by waterproofing" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/85 via-navy/70 to-navy-deep" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/40 to-transparent" />
      </motion.div>

      <Rain />

      {/* Glowing shield forming */}
      <motion.svg
        viewBox="0 0 200 240" className="absolute right-[6%] top-1/2 -translate-y-1/2 w-[280px] md:w-[420px] hidden md:block shield-glow"
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 0.9, scale: 1 }} transition={{ delay: 1.4, duration: 1.2, ease: "easeOut" }}
        aria-hidden
      >
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4DA8FF" />
            <stop offset="100%" stopColor="#005BFF" />
          </linearGradient>
        </defs>
        <motion.path
          d="M100 8 L188 44 V120 C188 180 148 216 100 232 C52 216 12 180 12 120 V44 Z"
          fill="url(#sg)" fillOpacity="0.08" stroke="url(#sg)" strokeWidth="2.5"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.4, duration: 1.6, ease: "easeInOut" }}
        />
        <motion.path
          d="M70 118 L92 142 L134 92" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 2.6, duration: 0.7 }}
        />
      </motion.svg>

      <motion.div style={{ y: contentY }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full pt-28 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full text-xs text-sky mb-6 tracking-wide"
        >
          <span className="h-2 w-2 rounded-full bg-[#25D366] animate-pulse" /> Jaipur's #1 Rated Waterproofing Brand
        </motion.div>

        <h1 className="font-display font-extrabold text-white leading-[0.95] tracking-tight text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskLine delay={0.3}>Jaipur's Trusted</MaskLine>
          <MaskLine delay={0.45} className="text-gradient-blue">Waterproofing Experts</MaskLine>
          <MaskLine delay={0.6} className="text-2xl sm:text-4xl lg:text-5xl font-bold mt-2">Protect Your Property</MaskLine>
          <MaskLine delay={0.72} className="text-2xl sm:text-4xl lg:text-5xl font-bold">Before Water Damage Begins.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="text-slate-200 text-base md:text-lg max-w-2xl mt-6 leading-relaxed"
        >
          Premium waterproofing for residential, commercial & industrial properties across Jaipur.
          12+ years of expertise, up to 15-year warranty and advanced systems that safeguard your investment for years to come.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15 }}
          className="flex flex-wrap items-center gap-3 mt-8"
        >
          <button onClick={() => openLead("book_inspection")} className="btn-ripple bg-royal hover:bg-royal/90 text-white font-semibold px-6 py-3.5 rounded-sm flex items-center gap-2 transition-colors" data-testid="hero-book">
            Book Free Inspection <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={() => openLead("get_quote")} className="btn-ripple glass text-white font-semibold px-6 py-3.5 rounded-sm hover:bg-white/10 transition-colors" data-testid="hero-quote">
            Get Instant Quote
          </button>
          <a href={`https://wa.me/${COMPANY.phoneRaw}`} className="glass text-white font-semibold px-5 py-3.5 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2" data-testid="hero-whatsapp">
            <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp
          </a>
          <a href={`tel:${COMPANY.phoneRaw}`} className="glass text-white font-semibold px-5 py-3.5 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2" data-testid="hero-callnow">
            <Phone className="h-4 w-4 text-sky" /> Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="flex flex-wrap gap-x-8 gap-y-3 mt-10"
        >
          {badges.map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-white">
              {b.icon ? <b.icon className="h-5 w-5 text-sky" /> : null}
              <div className="leading-tight">
                <div className="font-display font-bold text-sm md:text-base">{b.label}</div>
                {b.sub && <div className="text-[10px] uppercase tracking-wider text-slate-400">{b.sub}</div>}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
